import React, { useEffect, useState } from 'react';
import { BarChart3, Download, Calendar } from 'lucide-react';
import { dashboardAPI, monthlyAnalysisAPI, formatCurrency, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';

const ReportSummary = () => {
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [ytd, setYtd] = useState({});
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [isExporting, setIsExporting] = useState(false);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  useEffect(() => { fetchData(); }, [month]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [sumRes, ytdRes] = await Promise.all([
        dashboardAPI.getSummary(),
        monthlyAnalysisAPI.getYtdPerformance()
      ]);
      setSummary(sumRes?.data || {});
      setYtd(ytdRes?.data || {});
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load summary report');
      setSummary({});
      setYtd({});
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const monthName = months.find(m => m.value === month)?.label || 'Current';
    const filename = `summary-report-${monthName.toLowerCase()}`;
    const result = await PDFExporter.exportToPDF('report-summary-standard', filename, { scale: 1.5 });
    if (!result.success) alert('Failed to export PDF: ' + result.error);
    setIsExporting(false);
  };

  return (
    <div id="report-summary-standard" className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-blue-600" />
            Summary Report (Standard)
          </h1>
          <p className="text-gray-600 mt-2">High-level standard summary across subscribers, fees, and performance</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select value={month} onChange={(e)=>setMonth(parseInt(e.target.value))} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500">
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <button onClick={handleExportPDF} disabled={isExporting} className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm ${isExporting ? 'bg-gray-400 text-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            <Download className="h-4 w-4" />
            <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Total Subscribers</div>
          <div className="text-2xl font-bold">{formatNumber(summary.totalSubscribers || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Active Subscribers</div>
          <div className="text-2xl font-bold text-green-700">{formatNumber(summary.activeSubscribers || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Total Collections</div>
          <div className="text-2xl font-bold text-green-700">{formatCurrency(summary.totalCollections || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Compliance Rate</div>
          <div className="text-2xl font-bold">{Number.isFinite(Number(summary.complianceRate)) ? Number(summary.complianceRate).toFixed(1) : '0.0'}%</div>
        </div>
      </div>

      {/* YTD section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold">Year-to-Date Performance</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div>
            <div className="text-sm text-gray-600">YTD Revenue</div>
            <div className="text-xl font-bold">{formatCurrency(ytd.ytdRevenue || 0)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">YTD Subscribers Growth</div>
            <div className="text-xl font-bold">{formatNumber(ytd.ytdSubscribersGrowth || 0)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">YTD Avg Compliance</div>
            <div className="text-xl font-bold">{Number.isFinite(Number(ytd.ytdComplianceRate)) ? Number(ytd.ytdComplianceRate).toFixed(1) : '0.0'}%</div>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500">This is a standard format summary for regulatory reporting purposes.</div>
    </div>
  );
};

export default ReportSummary;
